import { store } from 'redux/store';
import { useTranslate } from 'hooks/useTranslate';
import { FC, ReactNode, createContext, useContext, useState } from 'react';

import { Note, PagePermissions } from 'types';

import asyncActionSwal from 'utils/asyncActionSwal';
import successSwal from 'utils/successSwal';
import errorSwal from 'utils/errorSwal';

import actionTypes from 'constants/actionTypes';

import { useGetNotesByReference } from 'modules/notes/hooks/useGetNotesByReference';
import { AddNoteModalProvider } from 'modules/_shared/components/Modals/AddNote/context/AddNoteModalContext';
import { deleteNote } from 'modules/notes/api/deleteNote';
import { setModal } from 'modules/_shared/redux/modalActions';
import AddNoteModal from 'modules/_shared/components/Modals/AddNote';

import { NotesSectionContextType } from '../types/NotesSectionContextType';
import { notesSectionInitValue } from '../constants/notesSectionInitValue';

const NotesSectionContext = createContext<NotesSectionContextType>(
  notesSectionInitValue
);

export const NotesSectionProvider: FC<{
  children: ReactNode;
  referenceId: string;
  referenceType: string;
  permissions: PagePermissions;
}> = ({ children, referenceId, referenceType, permissions }) => {
  const { t } = useTranslate();

  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [refetchKey, setRefetchKey] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const { data: notesData, isLoading: isLoadingNotes } = useGetNotesByReference(
    {
      referenceId,
      referenceType,
      page,
      rowsPerPage,
      refetchKey,
    }
  );

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  };

  const invalidateNotes = () => {
    setRefetchKey((v) => v + 1);
  };

  const handleAddNote = () => {
    store.dispatch(
      setModal(
        <AddNoteModalProvider
          referenceId={referenceId}
          referenceType={referenceType}
          modalActionType={actionTypes.ADD}
          onSuccess={invalidateNotes}
        >
          <AddNoteModal />
        </AddNoteModalProvider>
      )
    );
  };

  const handleEditNote = (note: Note) => {
    store.dispatch(
      setModal(
        <AddNoteModalProvider
          referenceId={referenceId}
          referenceType={referenceType}
          modalActionType={actionTypes.EDIT}
          noteToEdit={note}
          onSuccess={invalidateNotes}
        >
          <AddNoteModal />
        </AddNoteModalProvider>
      )
    );
  };

  const handleDeleteNote = async (noteId: string) => {
    asyncActionSwal({
      swalTitle: t('AreYouSureAboutDeletingNote'),
      swalElementName: '',
      swalInfoText: t('ThisProcedureCantUndo'),
      swalConfirmButtonText: t('OkDelete'),
      callback: async () => {
        try {
          setIsLoading(true);

          await store.dispatch(deleteNote({ noteId }));

          invalidateNotes();

          successSwal(t('NoteDeletedSuccessfully'));

          setIsLoading(false);
        } catch (error) {
          setIsLoading(false);
          errorSwal(t('ErrorDeletingNote'));
        }
      },
    });
  };

  return (
    <NotesSectionContext.Provider
      value={{
        data: {
          notes: notesData.notes || [],
          notesTotal: notesData?.notesTotal || 0,
          page,
          rowsPerPage,
          isAdmin: permissions?.isAdmin || false,
          isReadOnly: permissions?.isReadOnly || false,
          isLoadingNotes,
          isLoading,
        },
        actions: {
          handleChangePage,
          handleChangeRowsPerPage,
          handleAddNote,
          handleEditNote,
          handleDeleteNote,
        },
      }}
    >
      {children}
    </NotesSectionContext.Provider>
  );
};

export const useNotesSection = () => {
  const context = useContext(NotesSectionContext);
  if (!context) {
    throw new Error(
      'useNotesSection must be used within a NotesSectionProvider'
    );
  }
  return context;
};
